// The single compression implementation behind the `compress` / `decompress`
// builtins (docs/compression.md).
//
// One impl, pinned level, used by every runtime tier — the browser client, the
// Node server, the fidelity-rig headless client, and any future interpreter all
// reach this module through makeBaseEnv's universal tier (base-env.ts). That is
// the determinism contract: `compress` output is byte-identical everywhere the
// rig replays a script, because it is literally the same code at the same level
// on every tier. (There is deliberately no "native zlib on the server, a
// different lib in the browser" split.)
//
// fflate is a pure-JS, deterministic DEFLATE — same input + same level => same
// bytes. We use RAW deflate/inflate (no zlib/gzip container) for minimal
// overhead; any framing a script wants (version tag, checksum, length) it adds
// around the payload itself.
import { deflateSync, Inflate } from "fflate";
// Pinned compression level. Fixed by design — exposing a level knob would let
// `compress` output vary per call and widen the API for no real gain
// (docs/compression.md §"The determinism contract"). 6 is fflate's default: a
// good ratio/speed balance. Changing this value changes every future
// `compress` output, so treat it as part of the on-disk format.
const LEVEL = 6;
// Hard ceiling on decompress() output — the dedicated decompression-bomb guard.
// A DEFLATE stream can expand ~1000x, so a 1 KB blob can inflate to hundreds of
// MB and exhaust host memory. C1's wasm-heap cap only bounds the COPY of the
// result INTO the script's heap; the raw inflate happens host-side first, so it
// needs its own limit. 64 MiB is far above any legitimate script payload
// (assets are inlined base64, not multi-MB runtime blobs) and matches the wasm
// heap ceiling — a result that couldn't fit the heap anyway is rejected here
// gracefully instead of trapping on the copy. Determinism is unaffected: a
// well-formed stream under the cap decodes identically on every tier.
const MAX_DECOMPRESS_BYTES = 64 * 1024 * 1024;
// Feed the deflate stream to the streaming inflater in small slices. A single
// `push(blob, true)` decodes the WHOLE stream into one allocation before the
// sink sees a byte — defeating the cap; slicing makes fflate emit output
// incrementally, so the sink can abort (throw) once the running total passes
// the ceiling, bounding peak allocation to ~one slice past it.
const INFLATE_SLICE = 16 * 1024;
// compress(data) — raw DEFLATE at the pinned level. Always succeeds; the empty
// input compresses to a valid (tiny) empty stream that decompresses back to
// empty.
export function compressBytes(data) {
    return deflateSync(data, { level: LEVEL });
}
// decompress(blob) — inverse of compressBytes. DEFLATE decode is inherently
// deterministic (a valid stream has exactly one expansion), so nothing here is
// tier-dependent. Malformed input OR a stream that expands past
// MAX_DECOMPRESS_BYTES (a decompression bomb) must NOT crash or OOM the script's
// instance, so both swallow to an empty array — the child-proof contract the
// byte builtins keep. (A script that needs to tell "empty payload" from "bad/
// oversized blob" apart frames its own length marker; see docs/compression.md.)
// `maxBytes` defaults to the shipping ceiling; it's a parameter so tests can
// pin the boundary without allocating 64 MiB.
export function decompressBytes(blob, maxBytes = MAX_DECOMPRESS_BYTES) {
    try {
        const chunks = [];
        let total = 0;
        const inflate = new Inflate((chunk) => {
            total += chunk.length;
            // Over the ceiling → abort the inflate. Throwing from the sink unwinds
            // push(), so we stop decoding immediately (bounding CPU too, not just
            // memory) and land in the catch below.
            if (total > maxBytes)
                throw new RangeError("decompress: output exceeds cap");
            chunks.push(chunk);
        });
        for (let i = 0; i < blob.length; i += INFLATE_SLICE) {
            const end = Math.min(i + INFLATE_SLICE, blob.length);
            inflate.push(blob.subarray(i, end), end >= blob.length);
        }
        const out = new Uint8Array(total);
        let off = 0;
        for (const c of chunks) {
            out.set(c, off);
            off += c.length;
        }
        return out;
    }
    catch {
        return new Uint8Array(0);
    }
}
//# sourceMappingURL=codec.js.map